import {
  ProblemSeverity,
  ValidationArea,
  ValidationProblemCode,
  ValidationProblemResponse,
} from '@ydistri/api-sdk';
import { ValidatorMessage, ValidatorMessageSeverity, ValidatorSection } from './validatorLib';
import { passwordMessages } from '@ydistri/utils';

/**
 * The server's problem contract on the client: one identity per problem, `(Code, Target, Path)`, so a
 * server answer replaces the local copy of the same rule instead of showing twice, and two problems on
 * two paths stay two.
 */

export const sectionArea: Record<ValidatorSection, ValidationArea> = {
  [ValidatorSection.CATEGORIES]: ValidationArea.Categories,
  [ValidatorSection.REGIONS]: ValidationArea.Regions,
  [ValidatorSection.STORES_AND_DEPARTMENTS]: ValidationArea.StoresAndDepartments,
  [ValidatorSection.SKU_CLASSES]: ValidationArea.SkuClasses,
  [ValidatorSection.PRODUCT_LISTS]: ValidationArea.ProductLists,
  [ValidatorSection.BRANDS]: ValidationArea.Brands,
};

export type ProblemIdentity = Pick<ValidationProblemResponse, 'code' | 'target' | 'path'>;

export const problemKey = (p: ProblemIdentity): string =>
  `${p.code}|${p.target?.entityType ?? ''}:${p.target?.entityId ?? ''}|${p.path ?? ''}`;

export const severityToStatus = (severity: ProblemSeverity): ValidatorMessageSeverity => {
  switch (severity) {
    case ProblemSeverity.Error:
      return ValidatorMessageSeverity.ERROR;
    case ProblemSeverity.Warning:
      return ValidatorMessageSeverity.WARNING;
    case ProblemSeverity.Info:
      return ValidatorMessageSeverity.INFO;
  }
};

export const problemToMessage = (
  section: ValidatorSection,
  problem: ValidationProblemResponse,
): ValidatorMessage => ({
  section,
  status: severityToStatus(problem.severity),
  key: problemKey(problem),
  message: problem.message,
});

/** Server first; a local message whose key the server also answered is dropped. */
export const mergeProblems = <T extends { key: React.Key }>(local: T[], server: T[]): T[] => {
  const answered = new Set(server.map(m => m.key));
  return [...server, ...local.filter(m => !answered.has(m.key))];
};

export type ReportStatus = 'loading' | 'failed' | 'ready';

export interface ReportState {
  status: ReportStatus;
  problems: ValidationProblemResponse[];
}

/**
 * What a section shows for one report. A report that failed to load is an error the user sees, not
 * a clean section: missing, failed and refetching are never valid.
 */
export const reportMessages = (
  section: ValidatorSection,
  title: string,
  report: ReportState,
): ValidatorMessage[] => {
  if (report.status === 'failed') {
    return [
      {
        section,
        status: ValidatorMessageSeverity.ERROR,
        key: `${section}|unavailable`,
        message: `The ${title} check could not be loaded. Reload the page or try again later.`,
      },
    ];
  }
  if (report.status === 'loading') return [];
  // One store per problem would fill the popover with a list the stores screen already shows in
  // full (the flagged-stores modal); here the count is the message.
  const flagged = report.problems.filter(
    p => p.code === ValidationProblemCode.StoresAndDepartmentsFlaggedStores,
  );
  const messages = report.problems
    .filter(p => p.code !== ValidationProblemCode.StoresAndDepartmentsFlaggedStores)
    .map(p => problemToMessage(section, p));
  if (flagged.length > 0) {
    messages.push({
      section,
      status: ValidatorMessageSeverity.WARNING,
      key: `${section}|flagged`,
      message: `${flagged.length} ${flagged.length === 1 ? 'store' : 'stores'} in use with no recent sale or newly opened - see the stores page.`,
    });
  }
  return messages;
};

export const reportStatus = (query: {
  isFetching: boolean;
  isError: boolean;
  data?: unknown;
}): ReportStatus => {
  if (query.isFetching) return 'loading';
  if (query.isError || query.data === undefined) return 'failed';
  return 'ready';
};

/** Valid means every report is ready and nothing reported is an Error; warnings never block. */
export const isValid = (
  reports: ReportState[],
  errorCount: number,
  localLoading: boolean,
): boolean => !localLoading && reports.every(r => r.status === 'ready') && errorCount === 0;

export interface ValidatorVerdict {
  /** A report is still loading or refetching; a missing report is not a valid one. */
  isLoading: boolean;
  /** Errors across the reports; a report that failed to load counts as one. */
  errorCount: number;
  warningCount: number;
  /** Every report ready and no Error among them; warnings never block. */
  valid: boolean;
}

/** The verdict of a set of reports, read straight off their states - nothing in between. */
export const verdictOf = (reports: ReportState[]): ValidatorVerdict => {
  // Counted as the popover shows them (reportMessages): only a ready report's problems, flagged
  // stores once per report. A failed report may still carry its last data; it counts as the one
  // error below, not as its stale problems.
  const count = (severity: ProblemSeverity) =>
    reports.reduce((n, r) => {
      if (r.status !== 'ready') return n;
      const ofSeverity = r.problems.filter(p => p.severity === severity);
      const flagged = ofSeverity.filter(
        p => p.code === ValidationProblemCode.StoresAndDepartmentsFlaggedStores,
      ).length;
      return n + ofSeverity.length - flagged + (flagged > 0 ? 1 : 0);
    }, 0);
  const failed = reports.filter(r => r.status === 'failed').length;
  const errorCount = count(ProblemSeverity.Error) + failed;
  return {
    isLoading: reports.some(r => r.status === 'loading'),
    errorCount,
    warningCount: count(ProblemSeverity.Warning),
    valid: isValid(reports, errorCount, false),
  };
};

/**
 * The text of the local copies of server rules, keyed by the server's code so a renamed or removed
 * code fails the typecheck here instead of drifting.
 */
export const localProblemMessages: Partial<Record<ValidationProblemCode, string>> = {
  [ValidationProblemCode.CalculationTitleRequired]: 'Please input the title of calculation',
  [ValidationProblemCode.CalculationTemplateRequired]: 'Please select a template',
  [ValidationProblemCode.CalculationNoSubordinates]:
    'Add at least one template or manual calculation',
  [ValidationProblemCode.TemplateTitleRequired]: 'Please enter a title for your template',
  [ValidationProblemCode.CalculationSubmergedNotEditable]:
    'Submerged calculations are edited from their main calculation.',
  [ValidationProblemCode.StoreScheduleStoreRequired]: 'Pick a store.',
  [ValidationProblemCode.StoreScheduleRestrictionRequired]: 'Pick at least one restriction.',
  [ValidationProblemCode.StoreSchedulePeriodIncomplete]: 'Enter the period.',
  [ValidationProblemCode.StoreSchedulePeriodReversed]: 'The period ends before it starts.',
  [ValidationProblemCode.StoreSchedulePeriodEnded]: 'The period has already ended.',
  [ValidationProblemCode.EntityListNameRequired]: 'Enter title of the entity list',
  [ValidationProblemCode.EntityListNameTaken]:
    'Entity List with this name already exists, choose different name',
  [ValidationProblemCode.EntityListTypeRequired]: 'Enter type of the entity list',
  [ValidationProblemCode.AiPromptTitleRequired]: 'Enter a title.',
  [ValidationProblemCode.AiPromptContentRequired]: 'Enter the content.',
  [ValidationProblemCode.PasswordTooShort]: passwordMessages.tooShort(8),
  [ValidationProblemCode.PasswordTooWeak]: passwordMessages.tooWeak,
  [ValidationProblemCode.PasswordOldMismatch]: 'The old password is not right',
  [ValidationProblemCode.RecordImportNegativeQuantity]: 'Invalid quantity - must not be negative.',
  [ValidationProblemCode.RecordImportSameSourceAndTargetStore]:
    'Source and target store identifiers must not be the same.',
  [ValidationProblemCode.RecordImportSkuUsedAsSourceAndTarget]:
    'The product already moves through this store the other way',
};

export const messageFor = (code: ValidationProblemCode): string =>
  localProblemMessages[code] ?? code;

/** Problems grouped by the field they name, for a form to show them under the right input. */
export const problemsByPath = (
  problems: ValidationProblemResponse[] | undefined,
): Record<string, string[]> => {
  const byPath: Record<string, string[]> = {};
  (problems ?? []).forEach(p => {
    const path = p.path ?? '';
    byPath[path] = [...(byPath[path] ?? []), p.message];
  });
  return byPath;
};

/** The body of a refused write, off the error RTK hands a caller; nothing if it is not that shape. */
const errorDataOf = (error: unknown): object | undefined => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
  const response = error.response;
  if (typeof response !== 'object' || response === null || !('data' in response)) return undefined;
  const data = response.data;
  return typeof data === 'object' && data !== null ? data : undefined;
};

/** The problems a refused write came back with; none if the error is not that shape. */
export const problemsOfError = (error: unknown): ValidationProblemResponse[] => {
  const data = errorDataOf(error);
  if (data === undefined || !('Problems' in data)) return [];
  return Array.isArray(data.Problems) ? data.Problems : [];
};

/**
 * What to tell the user about a refused write: the problems' messages, else the flat messages the
 * API always sends, else the caller's fallback.
 */
export const problemMessagesOf = (error: unknown, fallback: string): string => {
  const problems = problemsOfError(error).map(p => p.message);
  if (problems.length > 0) return problems.join(' ');
  const data = errorDataOf(error);
  const messages =
    data !== undefined && 'Messages' in data && Array.isArray(data.Messages) ? data.Messages : [];
  const texts = messages.filter((m): m is string => typeof m === 'string');
  return texts.length > 0 ? texts.join(' ') : fallback;
};

export interface FieldErrors<N> {
  /** Ready for antd's form.setFields: the problems whose path the form knows, under its field. */
  fields: { name: N; errors: string[] }[];
  /** The messages of every other problem - the root, or a path the form has no input for. */
  other: string[];
}

/** Sorts a refusal's problems onto a form: `fieldOf` maps the server's path to the form's field name. */
export const fieldErrors = <N extends string>(
  problems: ValidationProblemResponse[],
  fieldOf: Record<string, N>,
): FieldErrors<N> => {
  const byField = new Map<N, string[]>();
  const other: string[] = [];
  problems.forEach(p => {
    const name = p.path === undefined || p.path === null ? undefined : fieldOf[p.path];
    if (name === undefined) {
      other.push(p.message);
    } else {
      byField.set(name, [...(byField.get(name) ?? []), p.message]);
    }
  });
  return { fields: [...byField.entries()].map(([name, errors]) => ({ name, errors })), other };
};
